/** @typedef {import('../index.js').InvalidDocData} InvalidDocData */
import InventoryTx, { inventoryTxSchema, InventoryTxTypes } from './inventoryTx.js';
import InventoryByProductVersion, { inventoryByProductVersionSchema } from './inventoryByProductVersion.js';
import InventoryCommitment, {
  getInventoryCommitmentId,
  inventoryCommitmentSchema,
} from './inventoryCommitment.js';
import ReorderSuggestion, { reorderSuggestionSchema } from './reorderSuggestion.js';

export {
  computeAggregateAfterDelta,
  computeCommitmentFulfillment,
  decideReorderAction,
  deriveCommitmentState,
} from './calculations.js';

export function getInventoryByProductVersionId(productDocId, productVersionId) {
  return `${productDocId}_${productVersionId}`;
}

function fromFirestore(Model, label, doc) {
  const docData = { id: doc.id, ...doc.data() };
  try {
    return Model.fromFirestore(docData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error transforming ${label} data:`, docData.id, error);
    /** @type {InvalidDocData} */
    const invalidDocData = { validation: 'failed', ...docData };
    return invalidDocData;
  }
}

function inventoryTxFromFirestore(doc) {
  return fromFirestore(InventoryTx, 'inventory transaction', doc);
}

function inventoryByProductVersionFromFirestore(doc) {
  return fromFirestore(InventoryByProductVersion, 'inventory by product version', doc);
}

function inventoryCommitmentFromFirestore(doc) {
  return fromFirestore(InventoryCommitment, 'inventory commitment', doc);
}

function reorderSuggestionFromFirestore(doc) {
  return fromFirestore(ReorderSuggestion, 'reorder suggestion', doc);
}

function inventoryTxToFirestore(inventoryTx, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  return new InventoryTx(inventoryTx).toFirestore({ serverTimestamp });
}

function inventoryByProductVersionToFirestore(inventoryByProductVersion, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  return new InventoryByProductVersion(inventoryByProductVersion).toFirestore({ serverTimestamp });
}

function inventoryCommitmentToFirestore(inventoryCommitment, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  return new InventoryCommitment(inventoryCommitment).toFirestore({ serverTimestamp });
}

function reorderSuggestionToFirestore(reorderSuggestion, serverTimestamp) {
  if (!serverTimestamp) {
    throw new Error('Missing serverTimestamp function');
  }
  return new ReorderSuggestion(reorderSuggestion).toFirestore({ serverTimestamp });
}

export {
  InventoryTx,
  InventoryTxTypes,
  InventoryByProductVersion,
  InventoryCommitment,
  ReorderSuggestion,
  getInventoryCommitmentId,
  inventoryTxSchema,
  inventoryByProductVersionSchema,
  inventoryCommitmentSchema,
  reorderSuggestionSchema,
  inventoryTxFromFirestore,
  inventoryTxToFirestore,
  inventoryByProductVersionFromFirestore,
  inventoryByProductVersionToFirestore,
  inventoryCommitmentFromFirestore,
  inventoryCommitmentToFirestore,
  reorderSuggestionFromFirestore,
  reorderSuggestionToFirestore,
};
